import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useWorkspaceKind } from '../workspace/useWorkspaceKind';
import { derivedFullName, greetingName, hasRealName, nameAskLabel, splitConfirmedName } from '../workspace/userName';
import { markNameNudgeDismissed } from '../components/NameNudge';
import { BrandLockup } from '../brand/BrandLockup';
import { useTour } from '../tour/TourProvider';
import { shouldShowWelcome } from './welcome';
import { btns } from '../components/buttons';
import { curatedErrorMessage } from '../lib/curatedError';
import { hasVisibleContent } from '@steward-app/steward-types';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';
import { useFocusTrap } from '../lib/useFocusTrap';

/**
 * The arrival moment — a one-time, full-surface welcome shown the first time a
 * user stands inside a workspace (founder fresh from bootstrap, or an invitee
 * fresh from accepting). Two quiet beats on one card-less cream field:
 *
 *   1. If the portal only knows them by an email-derived name: "what should we
 *      call you?" (same write path as NameNudge / Settings → Profile).
 *   2. The tour offer: a guided look around, or straight in. Either choice
 *      marks the welcome seen (cross-device, via profile preferences), so it
 *      never replays on its own; the tour stays reachable from the account menu.
 *
 * `?welcome=1` forces it open regardless of the stored preference (deep-link
 * re-entry + deterministic e2e); the param is stripped on close. Esc always
 * closes — an arrival, never a gate.
 *
 * ⚠️ **Who reaches it is {@link shouldShowWelcome}'s decision, not this file's, and it is
 * about the ACTIVE WORKSPACE as much as about the profile** (steward-app#1349). A visiting
 * advisor standing in a client's household is not arriving anywhere of their own, and this
 * card's copy ("for the benefit of your family") would be a false claim to them. Read that
 * function's header before adding a caller or widening `show`.
 *
 * ⚠️ **The forced `?welcome=1` arm below deliberately BYPASSES that gate**, so it is the one
 * path by which an advisor can still reach the family arrival copy inside a client's
 * household. Matt's ruling (2026-09-09) is that the bypass stays *provided nothing links to
 * it* — a link would turn "the person asked for this" into "the product sent them here".
 * That is a claim about the whole system, so it is a dated reading and not a property:
 * greps of `src/`, `services/` (including every mail composer — `inviteEmail.ts`,
 * `claimEmail.ts`, `founderInviteEmail.ts`, `mailSink.ts`), `packages/`, `deploy/`, `e2e/`
 * and `docs/` returned nine hits, none constructing a link (this change's independent
 * review, 2026-09-09); the coordinator separately searched all five sibling repos —
 * appcore, steward-site, crossmarc-site, crossmarc-ops, crossmarc-auth — with no hits at
 * all, same date. **Anything that ever links to `?welcome=1` invalidates the ruling, not
 * just this comment**: re-run both sweeps before adding one.
 */
export function WelcomeOverlay() {
  const { profile, activeWorkspaceId, activeMembership, updateProfile, setPreferences } = useWorkspace();
  const workspaceKind = useWorkspaceKind();
  const { start: startTour } = useTour();
  const [searchParams, setSearchParams] = useSearchParams();
  const forced = searchParams.get('welcome') === '1';

  const [closed, setClosed] = useState(false);
  // null until first shown; then locked in so a mid-flow profile refetch (the
  // name save) can't yank the phase from under the user.
  const [phase, setPhase] = useState<'name' | 'tour' | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The entrance animation is CSS-only (`.welcome-enter`, src/index.css). There is
  // deliberately no `settled` state here and no `requestAnimationFrame` — see
  // steward-app#1348 and that rule's comment: a JS flag gating the opacity of a
  // full-viewport opaque backdrop can lose its frame and blank the app.

  // Never show before /me resolves — the greeting and the name-phase decision
  // both need the profile (a forced open would otherwise race it and ask an
  // already-named user for their name). Once open (phase locked), stay open
  // until the user closes it: a background /me refetch that lands mid-flow
  // (window refocus, another tab dismissing) must not yank the overlay while
  // they're typing.
  const show = !closed && profile !== null
    && (forced || phase !== null || shouldShowWelcome({
      profile,
      workspaceId: activeWorkspaceId,
      membership: activeMembership,
      workspaceKind,
    }));

  // A forced deep link arriving after a close in the same mount re-opens it —
  // ?welcome=1 means "show me the welcome", whatever happened earlier.
  useEffect(() => {
    if (forced) setClosed(false);
  }, [forced]);

  useEffect(() => {
    if (show && phase === null) {
      const nextPhase = profile && hasRealName(profile) ? 'tour' : 'name';
      setPhase(nextPhase);
      // Seed with the full derived guess (e.g. "Matt Anderson") rather than the
      // one-word greeting, so the confirmation field shows the best available
      // guess instead of an empty box the user has to retype from scratch.
      if (nextPhase === 'name') setName(derivedFullName(profile));
    }
  }, [show, phase, profile]);

  function close(withTour: boolean) {
    // If they never told us a name, don't re-ask on the very next screen.
    // Ordered before the preference write: its optimistic cache update is what
    // re-renders NameNudge, which re-reads this flag at render time.
    if (profile && !hasRealName(profile)) markNameNudgeDismissed();
    // Persist once — a forced replay shouldn't overwrite the original timestamp.
    if (!profile?.preferences?.welcomeSeenAt) {
      void setPreferences({ welcomeSeenAt: new Date().toISOString() });
    }
    if (forced) {
      const next = new URLSearchParams(searchParams);
      next.delete('welcome');
      setSearchParams(next, { replace: true });
    }
    setClosed(true);
    if (withTour) startTour();
  }

  // Esc leaves quietly (same as "I'll explore on my own"). No dependency array
  // on purpose: `close` captures phase/profile/searchParams, so re-subscribing
  // each render keeps the handler current without threading a ref through.
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  const trapRef = useFocusTrap<HTMLDivElement>(show);

  if (!show) return null;

  async function handleNameSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Please enter a name.'); return; }
    if (!hasVisibleContent(trimmed)) { setError(INVISIBLE_NAME_MESSAGE); return; }
    setSaving(true); setError('');
    try {
      // givenName drives the short greeting, name drives the account menu — so
      // split the (possibly multi-word) field rather than writing the same
      // string to both, which would greet a confirmed "Matt Anderson" in full.
      // Shared with NameNudge's equivalent submit so the two surfaces can't
      // diverge on write behavior the way they once diverged on copy.
      await updateProfile(splitConfirmedName(trimmed));
      setPhase('tour');
    } catch (err) {
      // Default-deny (crossmarc/steward-app#2047): same `PUT /me` as Settings > Profile; the
      // server's `message` is never rendered.
      setError(curatedErrorMessage(err, {}, 'Could not save. Please try again.', 'Welcome name save'));
    } finally {
      setSaving(false);
    }
  }

  const greetName = greetingName(profile);

  return createPortal((
    <div
      data-testid="welcome"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome"
      className="fixed inset-0 z-[80] overflow-y-auto bg-cream"
      ref={trapRef}
    >
      <div
        data-testid="welcome-panel"
        className="welcome-enter mx-auto flex min-h-full w-full max-w-[460px] flex-col items-center justify-center px-6 py-16 text-center"
      >
        <BrandLockup />

        <h1 className="mb-2 mt-10 font-serif text-[30px] font-normal leading-tight tracking-[-0.01em] text-navy">
          Welcome{greetName ? `, ${greetName}` : ''}.
        </h1>
        <p className="m-0 font-sans text-[14px] leading-relaxed text-mid">
          Everything organized for the benefit of your family.
        </p>

        {phase === 'name' && (
          <form onSubmit={e => void handleNameSubmit(e)} className="mt-10 w-full max-w-[340px]">
            <label htmlFor="welcome-name" className="mb-2 block font-sans text-[13px] text-slate">
              {nameAskLabel(profile)}
            </label>
            <input
              id="welcome-name"
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              maxLength={100}
              className="box-border w-full rounded-token border border-line-strong bg-surface px-3 py-2.5 text-center font-sans text-[15px] text-navy outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
            />
            {error && <div className="mt-2 text-xs text-danger">{error}</div>}
            <div className="mt-4 flex flex-col items-center gap-3">
              <button type="submit" className={`${btns.btnPrim} w-full py-2.5`} disabled={saving}>
                {saving ? 'One moment…' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={() => { markNameNudgeDismissed(); setPhase('tour'); }}
                disabled={saving}
                className="cursor-pointer border-none bg-transparent p-0 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-slate transition-colors duration-150 hover:text-navy"
              >
                Skip for now
              </button>
            </div>
          </form>
        )}

        {phase === 'tour' && (
          <div className="mt-10 w-full max-w-[340px]">
            <p className="m-0 mb-5 font-sans text-[13px] leading-relaxed text-slate">
              Would you like a brief look around? It takes about a minute, and you can leave it at any point.
            </p>
            <div className="flex flex-col items-center gap-3">
              <button onClick={() => close(true)} className={`${btns.btnPrim} w-full py-2.5`}>
                Show me around
              </button>
              <button onClick={() => close(false)} className={`${btns.btn} w-full py-2.5`}>
                I'll explore on my own
              </button>
            </div>
            <p className="m-0 mt-5 font-sans text-[11px] text-slate">
              You can take the tour anytime from your account menu.
            </p>
          </div>
        )}
      </div>
    </div>
  ), document.body);
}
